<template>
  <div class="work-order-detail">
    <!-- 顶部导航 -->
    <div class="ls-card ls-release__header">
      <el-page-header @back="goBack" content="工单详情"></el-page-header>
    </div>

    <div class="detail-container" v-loading="loading">
      <!-- 基本信息 -->
      <el-card class="detail-card">
        <div slot="header" class="card-header">
          <span class="header-title">基本信息</span>
          <el-tag :type="getStatusTagType(detailData.order_status)" size="small">
            {{ detailData.order_status_name }}
          </el-tag>
        </div>

        <el-descriptions :column="2" border>
          <el-descriptions-item label="工单编号">
            {{ detailData.order_no || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="工单类型">
            <div class="type-info" v-if="detailData.workOrderType">
              <img v-if="detailData.workOrderType.icon" :src="detailData.workOrderType.icon" class="type-icon" />
              <span>{{ detailData.workOrderType.type_name }}</span>
            </div>
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="工单标题">
            {{ detailData.order_title || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="紧急程度">
            <el-tag :type="getUrgencyTagType(detailData.urgency_level)" size="mini">
              {{ detailData.urgency_level_name }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">
            {{ detailData.create_time || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="预计执行时间">
            {{ formatTime(detailData.scheduled_time) || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="支付状态">
            <el-tag :type="getPaymentTagType(detailData.payment_status)" size="mini">
              {{ detailData.payment_status_name }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="工单费用">
            <span class="price">¥{{ detailData.total_fee || '0.00' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="指派师傅" :span="2">
            <div v-if="detailData.assigned_staff && detailData.assigned_staff.length > 0" class="assigned-staff-list">
              <div v-for="staff in detailData.assigned_staff" :key="staff.work_order_staff_id != null ? staff.work_order_staff_id : staff.id" class="staff-item">
                <span class="staff-name">{{ staff.staff_name }}</span>
                <el-tag v-if="staff.is_primary === 1" size="mini" type="primary" class="m-l-4">主负责人</el-tag>
                <span class="m-l-10" v-if="staff.worker_type_name">{{ staff.worker_type_name }}</span>
                <span class="m-l-10" v-if="staff.salary_amount">薪酬：¥{{ staff.salary_amount }}</span>
                <span class="m-l-10" v-if="staff.actual_salary">实际薪酬：¥{{ staff.actual_salary }}</span>
                <el-tag v-if="staff.work_finished === 1" type="success" size="mini" class="m-l-8">已完结</el-tag>
                <el-tag v-else type="info" size="mini" class="m-l-8">施工中</el-tag>
                <el-button
                  v-if="staff.work_order_staff_id && [2, 3, 7, 8, 9].includes(detailData.order_status)"
                  :type="staff.work_finished === 1 ? 'default' : 'success'"
                  size="mini"
                  class="m-l-8"
                  :loading="staffSetFinishedLoading === staff.work_order_staff_id"
                  @click="handleSetStaffWorkFinished(staff)"
                >
                  {{ staff.work_finished === 1 ? '取消完结' : '标记施工完结' }}
                </el-button>
              </div>
            </div>
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="工单描述" :span="2">
            {{ detailData.order_description || '-' }}
          </el-descriptions-item>
        </el-descriptions>

        <!-- 操作按钮 -->
        <div class="action-buttons" style="margin-top: 20px; display: flex; gap: 10px;">
          <!-- 物料操作按钮 -->
          <el-button type="primary" size="small" @click="handleMaterialReturn">物料退回</el-button>
          <el-button type="success" size="small" @click="handleMaterialAdd">添加物料</el-button>

          <!-- 调换人手按钮 -->
          <el-button
            v-if="[2, 3, 7, 8, 9].includes(detailData.order_status)"
            type="warning"
            size="small"
            @click="handleChangeStaff"
          >
            调换人手
          </el-button>

          <!-- 验收按钮 -->
          <el-button
            v-if="detailData.current_node && detailData.current_node.action_type === 3 && detailData.order_status !== 5"
            type="danger"
            size="small"
            @click="handleAcceptance"
            :loading="acceptanceLoading"
          >
            验收
          </el-button>
        </div>
      </el-card>

      <!-- 报价单信息 -->
      <el-card class="detail-card" v-if="detailData.quotation">
        <div slot="header" class="card-header">
          <span class="header-title">报价单信息</span>
          <el-tag :type="getQuotationStatusTagType(detailData.quotation.status)" size="small">
            {{ detailData.quotation.status === 1 ? '待确认' : detailData.quotation.status === 2 ? '已确认' : detailData.quotation.status === 3 ? '已拒绝' : '已过期' }}
          </el-tag>
        </div>

        <el-descriptions :column="2" border>
          <el-descriptions-item label="报价单号">
            {{ detailData.quotation.quotation_no || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="报价标题">
            {{ detailData.quotation.title || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="总金额">
            <span class="price">¥{{ detailData.quotation.total_amount || '0.00' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="有效期">
            {{ detailData.quotation.valid_days || 0 }}天
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">
            {{ detailData.quotation.create_time || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="过期时间">
            {{ formatTime(detailData.quotation.expire_time) || '-' }}
          </el-descriptions-item>
        </el-descriptions>

        <div class="quotation-details" v-if="detailData.quotation.quotation_details && detailData.quotation.quotation_details.length > 0">
          <h4 class="details-title">报价明细</h4>
          <el-table :data="detailData.quotation.quotation_details" border style="width: 100%; margin-top: 10px;">
            <el-table-column prop="item_name" label="项目名称" min-width="180"></el-table-column>
            <el-table-column prop="item_description" label="描述" min-width="150"></el-table-column>
            <el-table-column prop="specification" label="规格" width="120"></el-table-column>
            <el-table-column prop="unit" label="单位" width="80"></el-table-column>
            <el-table-column prop="quantity" label="数量" width="80"></el-table-column>
            <el-table-column prop="unit_price" label="单价" width="100">
              <template slot-scope="scope">
                ¥{{ scope.row.unit_price || '0.00' }}
              </template>
            </el-table-column>
            <el-table-column prop="total_price" label="总价" width="100">
              <template slot-scope="scope">
                ¥{{ scope.row.total_price || '0.00' }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-card>

      <!-- 财务统计 -->
      <el-card class="detail-card" v-if="detailData.finance_statistics">
        <div slot="header" class="card-header">
          <span class="header-title">财务统计</span>
        </div>

        <!-- 物料成本 -->
        <h4 class="fs-section-title">物料成本</h4>
        <el-table
          :data="detailData.finance_statistics.materials"
          border
          style="width: 100%"
          show-summary
          :summary-method="getMaterialSummary"
        >
          <el-table-column prop="material_name" label="物料名称" min-width="160"></el-table-column>
          <el-table-column prop="unit" label="单位" width="80" align="center"></el-table-column>
          <el-table-column prop="quantity" label="数量" width="90" align="right"></el-table-column>
          <el-table-column prop="unit_cost" label="单位成本" width="110" align="right">
            <template slot-scope="scope">¥{{ scope.row.unit_cost || '0.00' }}</template>
          </el-table-column>
          <el-table-column prop="line_cost" label="成本合计" width="110" align="right">
            <template slot-scope="scope">¥{{ scope.row.line_cost || '0.00' }}</template>
          </el-table-column>
          <el-table-column prop="unit_sell" label="单位售价" width="110" align="right">
            <template slot-scope="scope">¥{{ scope.row.unit_sell || '0.00' }}</template>
          </el-table-column>
          <el-table-column prop="line_sell" label="售价合计" width="110" align="right">
            <template slot-scope="scope">¥{{ scope.row.line_sell || '0.00' }}</template>
          </el-table-column>
          <el-table-column prop="profit" label="利润" width="110" align="right">
            <template slot-scope="scope">
              <span class="fs-profit">¥{{ scope.row.profit || '0.00' }}</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 人工成本 -->
        <h4 class="fs-section-title fs-section-title--gap">人工成本</h4>
        <el-table
          :data="detailData.finance_statistics.labor"
          border
          style="width: 100%"
          show-summary
          :summary-method="getLaborSummary"
        >
          <el-table-column prop="staff_name" label="员工姓名" min-width="120"></el-table-column>
          <el-table-column prop="staff_no" label="工号" width="140"></el-table-column>
          <el-table-column prop="work_hours" label="工时(h)" width="90" align="right"></el-table-column>
          <el-table-column prop="salary_cost" label="薪酬成本" width="120" align="right">
            <template slot-scope="scope">¥{{ scope.row.salary_cost || '0.00' }}</template>
          </el-table-column>
          <el-table-column prop="quote_amount" label="报价金额" width="120" align="right">
            <template slot-scope="scope">¥{{ scope.row.quote_amount || '0.00' }}</template>
          </el-table-column>
          <el-table-column prop="profit" label="利润" width="110" align="right">
            <template slot-scope="scope">
              <span class="fs-profit">¥{{ scope.row.profit || '0.00' }}</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 合计 -->
        <div class="fs-grand-total" v-if="detailData.finance_statistics.grand_total">
          <div class="fs-grand-item">
            <span class="fs-grand-label">总成本</span>
            <span class="price">¥{{ detailData.finance_statistics.grand_total.total_cost }}</span>
          </div>
          <div class="fs-grand-item">
            <span class="fs-grand-label">总售价</span>
            <span class="price">¥{{ detailData.finance_statistics.grand_total.total_sell }}</span>
          </div>
          <div class="fs-grand-item">
            <span class="fs-grand-label">总利润</span>
            <span class="fs-profit fs-profit--lg">¥{{ detailData.finance_statistics.grand_total.total_profit }}</span>
          </div>
        </div>
      </el-card>

      <!-- 流程信息 -->
      <el-card class="detail-card" v-if="detailData.process_instance">
        <div slot="header" class="card-header">
          <span class="header-title">流程信息</span>
        </div>

        <el-descriptions :column="2" border>
          <el-descriptions-item label="流程实例">
            {{ detailData.process_instance.definition.definition_name || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="流程状态">
            <el-tag type="primary" size="mini">{{ detailData.process_instance.status_desc }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="开始时间">
            {{ detailData.process_instance.start_time || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="结束时间">
            {{ detailData.process_instance.end_time || '-' }}
          </el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 现场补货记录 -->
      <el-card class="detail-card" v-if="workOrderId">
        <div slot="header" class="card-header">
          <span class="header-title">现场补货记录</span>
        </div>
        <el-table :data="supplierSiteDeliveryList" border v-loading="supplierSiteDeliveryListLoading" style="width: 100%">
          <el-table-column prop="order_no" label="单号" width="140"></el-table-column>
          <el-table-column prop="supplier_name" label="供应商" width="150"></el-table-column>
          <el-table-column prop="material_name" label="物料名称" min-width="120"></el-table-column>
          <el-table-column prop="quantity" label="数量" width="80" align="center"></el-table-column>
          <el-table-column prop="amount" label="金额" width="100" align="right">
            <template slot-scope="scope">¥{{ (scope.row.amount || 0).toFixed(2) }}</template>
          </el-table-column>
          <el-table-column prop="delivery_status_text" label="送货状态" width="100"></el-table-column>
          <el-table-column prop="create_time_text" label="创建时间" width="160"></el-table-column>
          <el-table-column prop="remark" label="备注" min-width="100" show-overflow-tooltip v-if="supplierSiteDeliveryHasRemark"></el-table-column>
        </el-table>
        <div v-if="supplierSiteDeliveryList.length === 0 && !supplierSiteDeliveryListLoading" class="empty-tip">
          暂无现场补货记录
        </div>
      </el-card>

      <!-- 节点信息 -->
      <el-card class="detail-card" v-if="detailData.current_node || detailData.next_node">
        <div slot="header" class="card-header">
          <span class="header-title">节点信息</span>
        </div>

        <el-row :gutter="20">
          <el-col :span="12" v-if="detailData.current_node">
            <div class="node-card current-node">
              <div class="node-header">
                <i class="el-icon-time"></i>
                <span class="node-title">当前节点</span>
              </div>
              <div class="node-content">
                <div class="node-name">{{ detailData.current_node.node_name }}</div>
                <div class="node-desc">
                  <el-tag size="mini" type="info">{{ detailData.current_node.node_type_desc }}</el-tag>
                  <el-tag size="mini" type="primary">{{ detailData.current_node.action_type_desc }}</el-tag>
                </div>
              </div>
            </div>
          </el-col>

          <el-col :span="12" v-if="detailData.next_node">
            <div class="node-card next-node">
              <div class="node-header">
                <i class="el-icon-right"></i>
                <span class="node-title">下一节点</span>
              </div>
              <div class="node-content">
                <div class="node-name">{{ detailData.next_node.node_name }}</div>
                <div class="node-desc">
                  <el-tag size="mini" type="info">{{ detailData.next_node.node_type_desc }}</el-tag>
                  <el-tag size="mini" type="primary">{{ detailData.next_node.action_type_desc }}</el-tag>
                </div>
              </div>
            </div>
          </el-col>
        </el-row>
      </el-card>

      <!-- 审批记录 -->
      <el-card class="detail-card" v-if="detailData.approvals && detailData.approvals.length">
        <div slot="header" class="card-header">
          <span class="header-title">审批记录</span>
        </div>

        <el-timeline>
          <el-timeline-item
            v-for="(approval, index) in detailData.approvals"
            :key="approval.id"
            :timestamp="approval.start_time_desc"
            placement="top"
          >
            <el-card shadow="hover">
              <div class="approval-item">
                <div class="approval-header">
                  <span class="approval-node">{{ approval.node_name }}</span>
                  <el-tag
                    :type="getApprovalStatusTagType(approval.approval_status)"
                    size="mini"
                  >
                    {{ approval.approval_status_desc }}
                  </el-tag>
                </div>
                <div class="approval-content">
                  <div class="approval-type">类型: {{ approval.approval_type_desc }}</div>
                  <div class="approval-result" v-if="approval.approval_result">
                    结果: {{ approval.approval_result_desc }}
                  </div>
                  <div class="approval-comment" v-if="approval.approval_comment">
                    备注: {{ approval.approval_comment }}
                  </div>
                  <div class="approval-time" v-if="approval.end_time_desc">
                    完成时间: {{ approval.end_time_desc }}
                  </div>
                </div>
              </div>
            </el-card>
          </el-timeline-item>
        </el-timeline>
      </el-card>
    </div>

    <!-- 物料添加对话框 -->
    <el-dialog
      title="添加物料"
      :visible.sync="addMaterialDialogVisible"
      width="70%"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="material-selector">
        <el-form :inline="true" size="small">
          <el-form-item label="物料搜索">
            <el-input
              v-model="addMaterialQuery.keyword"
              placeholder="请输入物料名称/编号/条码/品牌"
              clearable
              @keyup.enter.native="getAddMaterialList"
            ></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="getAddMaterialList">查询</el-button>
          </el-form-item>
        </el-form>

        <el-table
          :data="addMaterialList"
          border
          style="width: 100%"
          v-loading="addMaterialPager.loading"
          @selection-change="handleAddMaterialSelection"
        >
          <el-table-column type="selection" width="55"></el-table-column>
          <el-table-column prop="material_name" label="物料名称"></el-table-column>
          <el-table-column prop="material_no" label="物料编号"></el-table-column>
          <el-table-column prop="unit" label="单位" width="80"></el-table-column>
          <el-table-column prop="stock" label="当前库存" width="100"></el-table-column>
          <el-table-column prop="specification" label="规格型号"></el-table-column>
          <el-table-column label="操作" width="160" fixed="right">
            <template slot-scope="scope">
              <el-button
                v-if="(scope.row.stock || 0) <= 0"
                type="warning"
                size="mini"
                @click="openSupplierSiteDeliveryForm(scope.row)"
              >
                申请供应商送货到现场
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container m-t-16">
          <el-pagination
            @size-change="handleAddMaterialSizeChange"
            @current-change="handleAddMaterialCurrentChange"
            :current-page="addMaterialPager.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="addMaterialPager.size"
            layout="total, sizes, prev, pager, next, jumper"
            :total="addMaterialPager.count"
          ></el-pagination>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="addMaterialDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAddMaterialSelection">确定选择</el-button>
      </div>
    </el-dialog>

    <!-- 物料退回对话框 -->
    <el-dialog
      title="物料退回"
      :visible.sync="returnMaterialDialogVisible"
      width="50%"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="return-material-form">
        <el-form :model="selectedReturnMaterial" label-width="120px" size="small">
          <el-form-item label="选择物料">
            <el-select
              v-model="selectedReturnMaterial"
              placeholder="请选择要退回的物料"
              style="width: 100%"
            >
              <el-option
                v-for="material in returnMaterialList"
                :key="material.id"
                :label="material.material_name + ' (' + material.material_no + ')'"
                :value="material"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="退回数量" v-if="selectedReturnMaterial">
            <el-input-number
              v-model="returnMaterialQuantity"
              :min="1"
              :max="selectedReturnMaterial.stock"
              :step="1"
              :precision="0"
              style="width: 100%"
            ></el-input-number>
            <div class="form-tip">库存数量: {{ selectedReturnMaterial.stock }} {{ selectedReturnMaterial.unit }}</div>
          </el-form-item>

          <el-form-item label="退回备注">
            <el-input
              v-model="returnMaterialRemark"
              type="textarea"
              placeholder="请输入退回原因等信息"
              :rows="3"
            ></el-input>
          </el-form-item>
        </el-form>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="returnMaterialDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="confirmReturnMaterial"
          :disabled="!selectedReturnMaterial || returnMaterialQuantity <= 0"
        >
          确认退回
        </el-button>
      </div>
    </el-dialog>

    <!-- 申请供应商送货到现场对话框 -->
    <el-dialog
      title="申请供应商送货到现场"
      :visible.sync="supplierSiteDeliveryDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      append-to-body
      @closed="resetSupplierSiteDeliveryForm"
    >
      <el-form :model="supplierSiteDeliveryForm" label-width="120px" size="small">
        <el-form-item label="物料">
          <el-input v-model="supplierSiteDeliveryForm.material_name" disabled placeholder="物料名称"></el-input>
        </el-form-item>
        <el-form-item label="补货数量" required>
          <el-input-number
            v-model="supplierSiteDeliveryForm.quantity"
            :min="1"
            :step="1"
            :precision="0"
            style="width: 100%"
          ></el-input-number>
          <div class="form-tip">希望供应商送到现场的数量，需大于0</div>
        </el-form-item>
        <el-form-item label="供应商">
          <el-select
            v-model="supplierSiteDeliveryForm.supplier_id"
            placeholder="不选则使用物料推荐供应商"
            clearable
            filterable
            style="width: 100%"
          >
            <el-option
              v-for="s in supplierList"
              :key="s.id"
              :label="s.supplier_name"
              :value="s.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="supplierSiteDeliveryForm.remark"
            type="textarea"
            placeholder="可选，说明补货原因等"
            :rows="2"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="supplierSiteDeliveryDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="confirmSupplierSiteDelivery"
          :loading="supplierSiteDeliverySubmitLoading"
          :disabled="!supplierSiteDeliveryForm.quantity || supplierSiteDeliveryForm.quantity <= 0"
        >
          提交申请
        </el-button>
      </div>
    </el-dialog>

    <!-- 验收对话框 -->
    <el-dialog
      title="工单验收"
      :visible.sync="acceptanceDialogVisible"
      width="50%"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="acceptance-form">
        <el-form :model="acceptanceData" label-width="120px" size="small">
          <el-form-item label="满意度评分">
            <el-rate v-model="acceptanceData.rating" :max="5" show-score></el-rate>
          </el-form-item>

          <el-form-item label="验收备注">
            <el-input
              v-model="acceptanceData.remark"
              type="textarea"
              placeholder="请输入验收备注，如工作完成质量等信息"
              :rows="4"
            ></el-input>
          </el-form-item>
        </el-form>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="acceptanceDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="confirmAcceptance"
        >
          确认验收
        </el-button>
      </div>
    </el-dialog>

    <!-- 调换人手对话框 -->
    <el-dialog
      title="调换人手"
      :visible.sync="changeStaffDialogVisible"
      width="1000px"
    >
      <div class="change-staff-section">
        <el-form :model="changeStaffForm" label-width="100px" size="small">
          <el-form-item label="工单信息">
            <div class="work-order-info">
              <div><strong>工单编号：</strong>{{ detailData.order_no }}</div>
              <div><strong>工单标题：</strong>{{ detailData.order_title }}</div>
              <div><strong>当前状态：</strong><el-tag :type="getStatusTagType(detailData.order_status)">{{ detailData.order_status_name }}</el-tag></div>
            </div>
          </el-form-item>

          <el-form-item label="员工列表">
            <div class="staff-list-section">
              <el-table
                :data="changeStaffForm.staffs"
                border
                style="width: 100%"
                v-loading="staffLoading"
                :header-cell-style="{ textAlign: 'center' }"
                :cell-style="{ textAlign: 'center' }"
              >
                <el-table-column prop="staff_name" label="员工姓名" width="150"></el-table-column>
                <el-table-column prop="employment_type" label="雇佣类型" width="120">
                  <template slot-scope="scope">
                    {{ scope.row.employment_type === 1 ? '全职直聘' : scope.row.employment_type === 2 ? '兼职' : scope.row.employment_type === 3 ? '临时工' : scope.row.employment_type === 4 ? '外包' : scope.row.employment_type === 5 ? '实习生' : '未知' }}
                  </template>
                </el-table-column>
                <el-table-column prop="duration_hours" label="工作时长(小时)" width="250"><template slot-scope="scope">
                    <el-input-number
                      v-model="scope.row.duration_hours"
                      :min="0.5"
                      :step="0.5"
                      size="small"
                      style="width: 100%"
                    ></el-input-number>
                  </template>
                </el-table-column>
                <el-table-column prop="labor_cost" label="人工成本" width="250"><template slot-scope="scope">
                    <el-input-number
                      v-model="scope.row.labor_cost"
                      :min="0"
                      :step="10"
                      size="small"
                      style="width: 100%"
                    ></el-input-number>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="80" fixed="right">
                  <template slot-scope="scope">
                    <el-button type="danger" size="small" @click="removeStaff(scope.$index)">删除</el-button>
                  </template>
                </el-table-column>
              </el-table>

              <div class="add-staff-section" style="margin-top: 20px;">
                <el-button type="primary" size="small" @click="openStaffSelector">添加员工</el-button>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="changeStaffDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitChangeStaff" :loading="changeStaffLoading">确定调换</el-button>
      </div>
    </el-dialog>

    <!-- 员工选择对话框 -->
    <el-dialog
      title="选择员工"
      :visible.sync="staffSelectorDialogVisible"
      width="800px"
    >
      <div class="staff-selector-section">
        <el-form :model="staffQuery" inline size="small" class="m-b-16">
          <el-form-item label="搜索">
            <el-input
              v-model="staffQuery.keyword"
              placeholder="请输入员工姓名、手机号"
              style="width: 300px;"
            ></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="getStaffList(1)">查询</el-button>
            <el-button @click="staffQuery.keyword = ''; getStaffList(1)">重置</el-button>
          </el-form-item>
        </el-form>

        <el-table
          :data="staffList"
          border
          style="width: 100%"
          v-loading="staffListLoading"
        >
          <el-table-column prop="staff_name" label="员工姓名" width="150"></el-table-column>
          <el-table-column prop="mobile" label="手机号" width="150"></el-table-column>
          <el-table-column prop="employment_type" label="雇佣类型" width="120">
            <template slot-scope="scope">
              {{ scope.row.employment_type === 1 ? '全职直聘' : scope.row.employment_type === 2 ? '兼职' : scope.row.employment_type === 3 ? '临时工' : scope.row.employment_type === 4 ? '外包' : scope.row.employment_type === 5 ? '实习生' : '未知' }}
            </template>
          </el-table-column>
          <el-table-column prop="worker_type_name" label="工种" width="120"></el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button type="primary" size="small" @click="addSelectedStaff(scope.row)">选择</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrapper m-t-16">
          <el-pagination
            @size-change="handleStaffSizeChange"
            @current-change="handleStaffCurrentChange"
            :current-page="staffPager.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="staffPager.size"
            layout="total, sizes, prev, pager, next, jumper"
            :total="staffPager.count"
          >
          </el-pagination>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { apiWorkOrderDetail, apiReturnMaterial, apiAddMaterial, apiWorkOrderAcceptance, apiWorkOrderChangeStaff, apiWorkOrderLaborInfo, apiSetStaffWorkFinished, apiRequestSupplierSiteDelivery, apiListSupplierSiteDelivery } from '@/api/work_order'
import { apiMaterialLists, apiSupplierList } from '@/api/material'
import { RequestPaging } from '@/utils/util'
import { apiStaffLists } from '@/api/staff'

export default {
  name: 'WorkOrderDetail',
  data () {
    return {
      loading: false,
      detailData: {
        order_no: '',
        order_type: null,
        order_title: '',
        urgency_level: null,
        order_status: null,
        payment_status: null,
        order_description: '',
        scheduled_time: null,
        actual_start_time: null,
        actual_end_time: null,
        total_fee: '',
        create_time: '',
        urgency_level_name: '',
        order_status_name: '',
        payment_status_name: '',
        workOrderType: null,
        workerType: null,
        process_instance: null,
        current_node: null,
        next_node: null,
        approvals: [],
        quotation: null,
        finance_statistics: null
      },

      // 物料添加相关数据
      addMaterialDialogVisible: false,
      addMaterialLoading: false,
      addMaterialQuery: { keyword: '' },
      addMaterialList: [],
      addMaterialPager: {
        page: 1,
        size: 10,
        lists: [],
        count: 0,
        loading: false
      },
      tempAddMaterials: [],

      // 物料退回相关数据
      returnMaterialDialogVisible: false,
      returnMaterialLoading: false,
      returnMaterialList: [],
      selectedReturnMaterial: null,
      returnMaterialQuantity: 0,
      returnMaterialRemark: '',

      // 验收相关数据
      acceptanceDialogVisible: false,
      acceptanceLoading: false,
      acceptanceData: {
        rating: 5,
        remark: ''
      },

      // 调换人手相关
      changeStaffDialogVisible: false,
      changeStaffLoading: false,
      changeStaffForm: {
        staffs: []
      },
      staffLoading: false,

      // 员工选择相关
      staffSelectorDialogVisible: false,
      staffList: [],
      staffListLoading: false,
      staffQuery: {
        keyword: ''
      },
      staffPager: new RequestPaging(),

      // 标记施工完结加载状态（当前操作中的 work_order_staff_id）
      staffSetFinishedLoading: 0,

      // 现场补货相关
      supplierSiteDeliveryList: [],
      supplierSiteDeliveryListLoading: false,
      supplierSiteDeliveryHasRemark: false,
      supplierSiteDeliveryDialogVisible: false,
      supplierSiteDeliverySubmitLoading: false,
      supplierSiteDeliveryForm: {
        material_id: null,
        material_name: '',
        quantity: 1,
        supplier_id: null,
        remark: ''
      },
      supplierList: []
    }
  },
  computed: {
    workOrderId () {
      return this.$route.params.id || this.$route.query.id
    }
  },
  created () {
    this.getDetailData()
  },
  methods: {
    // 获取详情数据
    getDetailData () {
      const id = this.$route.params.id || this.$route.query.id
      if (!id) {
        this.$message.error('缺少工单ID参数')
        this.goBack()
        return
      }

      this.loading = true
      apiWorkOrderDetail({ id })
        .then(data => {
          this.detailData = data || this.detailData
          this.getSupplierSiteDeliveryList()
        })
        .catch(error => {
          console.error('获取工单详情错误:', error)
          this.$message.error('网络错误，请稍后重试')
        })
        .finally(() => {
          this.loading = false
        })
    },

    // 返回上一页
    goBack () {
      this.$router.go(-1)
    },

    // 标记师傅施工完结 / 取消完结
    handleSetStaffWorkFinished (staff) {
      if (!staff.work_order_staff_id) {
        this.$message.warning('缺少工单师傅关联ID')
        return
      }
      const newFinished = staff.work_finished === 1 ? 0 : 1
      const action = newFinished === 1 ? '标记施工完结' : '取消完结'
      this.$confirm(`确定要${action}吗？${newFinished === 0 ? '（不会删除已生成的进度款记录）' : ''}`, '提示', {
        type: 'warning'
      }).then(() => {
        this.staffSetFinishedLoading = staff.work_order_staff_id
        apiSetStaffWorkFinished({
          work_order_staff_id: staff.work_order_staff_id,
          work_finished: newFinished
        }).then(() => {
          this.$message.success(`${action}成功`)
          staff.work_finished = newFinished
          this.$forceUpdate()
        }).catch(() => {
          this.$message.error(`${action}失败`)
        }).finally(() => {
          this.staffSetFinishedLoading = 0
        })
      }).catch(() => {})
    },

    // 格式化时间戳
    formatTime (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleString()
    },

    // 获取状态标签类型
    getStatusTagType (status) {
      const typeMap = {
        1: 'warning', // 待指派
        2: 'primary', // 已指派
        3: 'success', // 进行中
        4: 'info', // 已完成
        5: 'danger' // 已取消
      }
      return typeMap[status] || 'info'
    },

    // 获取紧急程度标签类型
    getUrgencyTagType (level) {
      const typeMap = {
        1: 'info', // 普通
        2: 'warning', // 紧急
        3: 'danger' // 非常紧急
      }
      return typeMap[level] || 'info'
    },

    // 获取支付状态标签类型
    getPaymentTagType (status) {
      const typeMap = {
        1: 'success', // 已支付
        2: 'warning', // 待支付
        3: 'info', // 部分支付
        4: 'danger' // 已退款
      }
      return typeMap[status] || 'info'
    },

    // 获取审批状态标签类型
    getApprovalStatusTagType (status) {
      const typeMap = {
        1: 'warning', // 待处理
        2: 'success', // 已通过
        3: 'danger', // 已驳回
        4: 'info' // 已转交
      }
      return typeMap[status] || 'info'
    },

    // 获取报价单状态标签类型
    getQuotationStatusTagType (status) {
      const typeMap = {
        1: 'warning', // 待确认
        2: 'success', // 已确认
        3: 'danger', // 已拒绝
        4: 'info' // 已过期
      }
      return typeMap[status] || 'info'
    },

    // 物料退回处理方法
    handleMaterialReturn () {
      const id = this.$route.params.id || this.$route.query.id
      if (!id) {
        return
      }

      // 打开物料退回对话框
      this.returnMaterialDialogVisible = true
      // 这里可以根据实际情况获取工单已使用的物料列表
      // 暂时使用物料库的物料列表作为示例
      this.getReturnMaterialList()
    },

    // 物料添加处理方法
    handleMaterialAdd () {
      const id = this.$route.params.id || this.$route.query.id
      if (!id) {
        return
      }

      // 打开物料添加对话框
      this.addMaterialDialogVisible = true
      this.getAddMaterialList()
      if (this.supplierList.length === 0) {
        apiSupplierList({ page: 1, size: 500 }).then((res) => {
          this.supplierList = res.lists || res || []
        })
      }
    },

    // 获取添加物料列表
    getAddMaterialList (page = 1) {
      this.addMaterialPager.loading = true
      this.addMaterialPager.page = page

      apiMaterialLists({
        page: this.addMaterialPager.page,
        size: this.addMaterialPager.size,
        ...this.addMaterialQuery
      }).then((res) => {
        this.addMaterialList = res.lists || []
        this.addMaterialPager.count = res.count || 0
        this.addMaterialPager.loading = false
      }).catch(() => {
        this.addMaterialPager.loading = false
      })
    },

    // 处理添加物料选择
    handleAddMaterialSelection (selection) {
      this.tempAddMaterials = selection
    },

    // 处理添加物料分页大小变化
    handleAddMaterialSizeChange (size) {
      this.addMaterialPager.size = size
      this.getAddMaterialList(1)
    },

    // 处理添加物料页码变化
    handleAddMaterialCurrentChange (page) {
      this.getAddMaterialList(page)
    },

    // 确认添加物料选择
    confirmAddMaterialSelection () {
      if (this.tempAddMaterials.length === 0) {
        this.$message.warning('请选择要添加的物料')
        return
      }

      const id = this.$route.params.id || this.$route.query.id
      if (!id) {
        return
      }

      // 为每个选择的物料打开数量输入对话框
      const processMaterial = (index) => {
        if (index >= this.tempAddMaterials.length) {
          // 所有物料处理完成
          this.addMaterialDialogVisible = false
          this.tempAddMaterials = []
          // 刷新页面数据
          this.getDetailData()
          return
        }

        const material = this.tempAddMaterials[index]

        this.$prompt(`请输入添加数量（当前库存：${material.stock} ${material.unit}）${(material.stock || 0) <= 0 ? '，库存不足时可点「申请供应商送货到现场」' : ''}`, `添加物料：${material.material_name}`, {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputValidator: (value) => {
            const quantity = Number(value)
            if (!quantity || quantity <= 0) {
              return '添加数量必须大于0'
            }
            return true
          }
        }).then(({ value }) => {
          const quantity = Number(value)

          // 调用添加物料接口
          apiAddMaterial({
            work_order_id: Number(id),
            material_id: material.id,
            quantity,
            remark: ''
          }).then(() => {
            this.$message.success('添加物料成功')
            processMaterial(index + 1)
          }).catch((err) => {
            const errMsg = (err && (err.msg || err.message || err)) || ''
            const isStockError = String(errMsg).indexOf('库存不足') >= 0 || String(errMsg).indexOf('库存不够') >= 0
            if (isStockError) {
              this.$confirm('库存不足，无法添加。是否申请供应商送货到现场？', '添加物料失败', {
                confirmButtonText: '申请供应商送货到现场',
                cancelButtonText: '取消',
                type: 'warning'
              }).then(() => {
                this.addMaterialDialogVisible = false
                this.tempAddMaterials = []
                this.openSupplierSiteDeliveryForm({ ...material, suggested_supplier_id: material.suggested_supplier_id }, quantity)
                processMaterial(this.tempAddMaterials.length)
              }).catch(() => {
                processMaterial(index + 1)
              })
            } else {
              this.$message.error(errMsg || '添加物料失败')
              processMaterial(index + 1)
            }
          })
        }).catch(() => {
          // 取消操作，继续处理下一个物料
          processMaterial(index + 1)
        })
      }

      // 开始处理第一个物料
      processMaterial(0)
    },

    // 获取退回物料列表
    getReturnMaterialList () {
      this.returnMaterialLoading = true

      apiMaterialLists({
        page: 1,
        size: 100
      }).then((res) => {
        this.returnMaterialList = res.lists || []
        this.returnMaterialLoading = false
      }).catch(() => {
        this.returnMaterialLoading = false
      })
    },

    // 确认退回物料
    confirmReturnMaterial () {
      if (!this.selectedReturnMaterial) {
        this.$message.warning('请选择要退回的物料')
        return
      }

      if (this.returnMaterialQuantity <= 0) {
        this.$message.warning('退回数量必须大于0')
        return
      }

      const id = this.$route.params.id || this.$route.query.id
      if (!id) {
        return
      }

      // 调用退回物料接口
      apiReturnMaterial({
        work_order_id: Number(id),
        material_id: this.selectedReturnMaterial.id,
        quantity: this.returnMaterialQuantity,
        remark: this.returnMaterialRemark
      }).then(() => {
        this.$message.success('物料退回成功')
        this.returnMaterialDialogVisible = false
        this.selectedReturnMaterial = null
        this.returnMaterialQuantity = 0
        this.returnMaterialRemark = ''
        this.getDetailData()
      }).catch(() => {})
    },

    // 获取现场补货单列表
    getSupplierSiteDeliveryList () {
      const id = this.$route.params.id || this.$route.query.id
      if (!id) return
      this.supplierSiteDeliveryListLoading = true
      apiListSupplierSiteDelivery({ work_order_id: Number(id) })
        .then((res) => {
          this.supplierSiteDeliveryList = res.lists || res || []
          this.supplierSiteDeliveryHasRemark = this.supplierSiteDeliveryList.some(item => item.remark != null && item.remark !== '')
        })
        .catch(() => {
          this.supplierSiteDeliveryList = []
        })
        .finally(() => {
          this.supplierSiteDeliveryListLoading = false
        })
    },

    // 打开申请供应商送货表单（从库存不足物料进入）
    openSupplierSiteDeliveryForm (material, quantity) {
      this.supplierSiteDeliveryForm = {
        material_id: material.id,
        material_name: `${material.material_name} (${material.material_no || ''})`,
        quantity: quantity || 1,
        supplier_id: material.suggested_supplier_id || null,
        remark: ''
      }
      if (this.supplierList.length === 0) {
        apiSupplierList({ page: 1, size: 500 }).then((res) => {
          this.supplierList = res.lists || res || []
        })
      }
      this.supplierSiteDeliveryDialogVisible = true
    },

    // 重置申请表单
    resetSupplierSiteDeliveryForm () {
      this.supplierSiteDeliveryForm = {
        material_id: null,
        material_name: '',
        quantity: 1,
        supplier_id: null,
        remark: ''
      }
    },

    // 确认申请供应商送货
    confirmSupplierSiteDelivery () {
      const id = this.$route.params.id || this.$route.query.id
      if (!id || !this.supplierSiteDeliveryForm.material_id) {
        this.$message.warning('参数不完整')
        return
      }
      if (!this.supplierSiteDeliveryForm.quantity || this.supplierSiteDeliveryForm.quantity <= 0) {
        this.$message.warning('补货数量必须大于0')
        return
      }
      this.supplierSiteDeliverySubmitLoading = true
      const params = {
        work_order_id: Number(id),
        material_id: this.supplierSiteDeliveryForm.material_id,
        quantity: this.supplierSiteDeliveryForm.quantity,
        remark: this.supplierSiteDeliveryForm.remark || undefined
      }
      if (this.supplierSiteDeliveryForm.supplier_id) {
        params.supplier_id = this.supplierSiteDeliveryForm.supplier_id
      }
      apiRequestSupplierSiteDelivery(params)
        .then((res) => {
          this.$message.success(`申请成功！单号：${res.order_no || ''}，供应商：${res.supplier_name || ''}`)
          this.supplierSiteDeliveryDialogVisible = false
          this.getSupplierSiteDeliveryList()
          this.getDetailData()
        })
        .catch(() => {})
        .finally(() => {
          this.supplierSiteDeliverySubmitLoading = false
        })
    },

    // 处理验收操作
    handleAcceptance () {
      this.acceptanceDialogVisible = true
    },

    // 确认验收
    confirmAcceptance () {
      const id = this.$route.params.id || this.$route.query.id
      if (!id) {
        return
      }

      this.acceptanceLoading = true

      // 调用验收接口
      apiWorkOrderAcceptance({
        id: Number(id),
        rating: this.acceptanceData.rating,
        remark: this.acceptanceData.remark
      }).finally(() => {
        this.acceptanceLoading = false
        // 关闭对话框并重置数据
        this.acceptanceDialogVisible = false
        this.acceptanceData = {
          rating: 5,
          remark: ''
        }
        // 刷新页面数据
        this.getDetailData()
      })
    },

    // 打开调换人手对话框
    handleChangeStaff () {
      const id = this.$route.params.id || this.$route.query.id
      if (!id) return

      // 调用获取工单人工费用信息接口
      this.staffLoading = true
      apiWorkOrderLaborInfo({ id })
        .then(res => {
          // 初始化员工列表，使用接口返回的数据
          this.changeStaffForm.staffs = (res.staffs || []).map(staff => ({
            id: staff.id,
            staff_name: staff.staff_name,
            employment_type: staff.employment_type,
            duration_hours: staff.duration_hours || 1.0,
            labor_cost: staff.labor_cost || 0
          }))
          this.changeStaffDialogVisible = true
        })
        .catch(error => {
          console.error('获取工单人工费用信息错误:', error)
          this.$message.error('网络错误，请稍后重试')
        })
        .finally(() => {
          this.staffLoading = false
        })
    },

    // 打开员工选择对话框
    openStaffSelector () {
      this.staffSelectorDialogVisible = true
      this.getStaffList(1)
    },

    // 获取员工列表
    getStaffList (page) {
      page && (this.staffPager.page = page)
      this.staffListLoading = true
      this.staffPager
        .request({
          callback: apiStaffLists,
          params: {
            ...this.staffQuery
          }
        })
        .then((res) => {
          this.staffList = res.lists || []
        })
        .finally(() => {
          this.staffListLoading = false
        })
    },

    // 添加选中的员工
    addSelectedStaff (staff) {
      // 检查是否已存在
      const exists = this.changeStaffForm.staffs.some((item) => item.id === staff.id)
      if (exists) {
        this.$message.warning('该员工已在列表中')
        return
      }

      this.changeStaffForm.staffs.push({
        id: staff.id,
        staff_name: staff.staff_name,
        employment_type: staff.employment_type,
        duration_hours: 1.0,
        labor_cost: 0
      })
      this.staffSelectorDialogVisible = false
    },

    // 删除员工
    removeStaff (index) {
      this.changeStaffForm.staffs.splice(index, 1)
    },

    // 提交调换人手
    submitChangeStaff () {
      if (this.changeStaffForm.staffs.length === 0) {
        this.$message.warning('请至少选择一名员工')
        return
      }

      const id = this.$route.params.id || this.$route.query.id
      if (!id) return

      this.changeStaffLoading = true
      apiWorkOrderChangeStaff({
        id: id,
        staffs: this.changeStaffForm.staffs
      }).then(res => {
        // this.$message.success('调换人手成功')
        // 调用获取工单人工费用信息接口更新数据
        return apiWorkOrderLaborInfo({ id })
      }).then(res => {
        // 更新员工列表
        this.changeStaffForm.staffs = (res.staffs || []).map(staff => ({
          id: staff.id,
          staff_name: staff.staff_name,
          employment_type: staff.employment_type,
          duration_hours: staff.duration_hours || 1.0,
          labor_cost: staff.labor_cost || 0
        }))
        this.changeStaffDialogVisible = false
        this.getDetailData()
      }).catch(error => {
        console.error('调换人手错误:', error)
        // this.$message.error('网络错误，请稍后重试')
      }).finally(() => {
        this.changeStaffLoading = false
      })
    },

    // 处理员工分页大小变化
    handleStaffSizeChange (size) {
      this.staffPager.size = size
      this.getStaffList()
    },

    // 处理员工分页页码变化
    handleStaffCurrentChange (page) {
      this.staffPager.page = page
      this.getStaffList()
    },

    getMaterialSummary () {
      const s = (this.detailData.finance_statistics || {}).material_summary || {}
      // [label, unit, qty, unit_cost(blank), line_cost, unit_sell(blank), line_sell, profit]
      return ['小计', '', '', '', `¥${s.total_cost || '0.00'}`, '', `¥${s.total_sell || '0.00'}`, `¥${s.total_profit || '0.00'}`]
    },

    getLaborSummary () {
      const s = (this.detailData.finance_statistics || {}).labor_summary || {}
      // [label, staff_no, work_hours, salary_cost, quote_amount, profit]
      return ['小计', '', '', `¥${s.total_salary_cost || '0.00'}`, `¥${s.total_quote || '0.00'}`, `¥${s.total_profit || '0.00'}`]
    }
  }
}
</script>

<style lang="scss" scoped>
.work-order-detail {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.detail-container {
    margin: 0 auto;
  }

  .ls-release__header {
    margin-bottom: 20px;
  }

.detail-card {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-title {
    font-weight: bold;
    font-size: 16px;
  }
}

.type-info {
  display: flex;
  align-items: center;

  .type-icon {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }
}

.price {
  color: #f56c6c;
  font-weight: bold;
  font-size: 16px;
}

.assigned-staff-list {
  .staff-item {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 10px;
    padding: 8px 12px;
    background: #f9fafc;
    border-radius: 4px;

    .staff-name {
      font-weight: 500;
    }
  }
}

.node-card {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 15px;
  height: 100%;

  &.current-node {
    border-left: 4px solid #409eff;
    background-color: rgba(64, 158, 255, 0.05);
  }

  &.next-node {
    border-left: 4px solid #67c23a;
    background-color: rgba(103, 194, 58, 0.05);
  }

  .node-header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    i {
      margin-right: 8px;
      color: #409eff;
    }

    .node-title {
      font-weight: bold;
    }
  }

  .node-content {
    .node-name {
      font-size: 16px;
      margin-bottom: 8px;
      font-weight: 500;
    }

    .node-desc {
      display: flex;
      gap: 8px;
    }
  }
}

// 审批记录样式调整
.detail-card:nth-child(3) {
  .el-card__body {
    padding: 10px 15px !important;
  }

  .el-timeline {
    .el-timeline-item {
      .el-timeline-item__node {
        top: 5px;
      }

      .el-timeline-item__wrapper {
        padding-left: 28px;

        .el-timeline-item__content {
          .el-card {
            margin-bottom: 8px;

            .el-card__body {
              padding: 8px 12px !important;
            }
          }
        }

        .el-timeline-item__timestamp {
          margin-bottom: 5px;
          font-size: 12px;
        }
      }
    }
  }
}

.approval-item {
  .approval-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;

    .approval-node {
      font-weight: bold;
      font-size: 14px;
    }
  }

  .approval-content {
        font-size: 12px;
        color: #606266;

        > div {
          margin-bottom: 3px;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    // 报价单样式
    .quotation-details {
      margin-top: 20px;

      .details-title {
        font-size: 14px;
        font-weight: bold;
        margin-bottom: 10px;
        color: #303133;
      }

      .el-table {
        .el-table__header-wrapper {
          th {
            background-color: #f5f7fa;
          }
        }
      }
    }

    .empty-tip {
      text-align: center;
      color: #909399;
      padding: 20px 0;
      font-size: 14px;
    }

    // 物料选择器样式
    .material-selector {
      .pagination-container {
        text-align: right;
        margin-top: 16px;
      }
    }

    // 物料退回表单样式
    .return-material-form {
      .form-tip {
        color: #909399;
        font-size: 12px;
        margin-top: 5px;
      }
    }

    // 财务统计卡片
    .fs-section-title {
      font-size: 14px;
      font-weight: bold;
      color: #303133;
      margin: 0 0 10px 0;

      &--gap {
        margin: 20px 0 10px 0;
      }
    }

    .fs-profit {
      color: #67c23a;
      font-weight: bold;

      &--lg {
        font-size: 18px;
      }
    }

    .fs-grand-total {
      display: flex;
      gap: 40px;
      align-items: center;
      margin-top: 20px;
      padding: 16px 20px;
      background: #f5f7fa;
      border-radius: 4px;

      .fs-grand-item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
      }

      .fs-grand-label {
        font-size: 12px;
        color: #909399;
      }
    }
</style>
